import { useCallback } from 'react';
import { useDeleteAutoApplyApplicationDraftMutation } from './__generated__/DeleteAutoApplyApplicationDraft.generated';
import { useInitialState } from '../../../lib/context/InitialStateContext';

// cors issue is being resolved https://indeed.atlassian.net/browse/IAFE-1104
export const dislikeJobRequest = async (indeedBaseUrl: string, jobKey: string): Promise<void> => {
    const url = `${indeedBaseUrl}/rpc/dislikeJob?pageId=auto-apply&jobKey=${encodeURIComponent(jobKey)}`;
    await fetch(url, { method: 'POST', credentials: 'include' });
};

interface UseDeleteAutoApplyApplicationDraftOptions {
    dislikeJob?: (indeedBaseUrl: string, jobKey: string) => Promise<void>;
}

/**
 * Deletes an application draft, and — only when a `jobKey` is given — also dislikes the job.
 *
 * Pass the `jobKey` for a **user-initiated** deletion (the JobInfoHeader trash button, the
 * ConfirmationPage remove button): the jobseeker is rejecting the job, so hide it from future search.
 * Omit it (or pass null) for **system auto-deletes** where the jobseeker never chose to reject the
 * job and it must not be disliked.
 *
 * The returned promise reflects the **delete mutation** only. The client's default
 * `mutate.errorPolicy` is `'all'` (a GraphQL-error response would resolve with `errors` in the
 * result), so this call overrides it to `'none'` — the promise then rejects on both network and
 * GraphQL errors, letting callers tell a real success from a failure instead of optimistically
 * treating every attempt as done. The `dislikeJob` RPC is fired best-effort and its failures are
 * swallowed — it must never block or fail the delete. Callers that don't care about the outcome
 * should still `.catch()` the returned promise (system auto-deletes are best-effort cleanup).
 */
export const useDeleteAutoApplyApplicationDraft = ({
    dislikeJob = dislikeJobRequest,
}: UseDeleteAutoApplyApplicationDraftOptions = {}) => {
    const { indeedBaseUrl } = useInitialState();
    const [deleteApplicationDraft] = useDeleteAutoApplyApplicationDraftMutation();

    return useCallback(
        /** @param jobKey provide ONLY for user-initiated deletes — its presence triggers dislikeJob. */
        (draftId: string, jobKey?: string | null) => {
            // Best-effort, non-blocking: a failed recommender RPC must not fail the delete.
            if (jobKey) void dislikeJob(indeedBaseUrl, jobKey).catch(() => {});
            // Return the delete mutation so callers await its actual success/failure. `errorPolicy:
            // 'none'` overrides the client default ('all') so a GraphQL-error response rejects.
            return deleteApplicationDraft({
                variables: { input: { applicationDraftId: draftId } },
                errorPolicy: 'none',
            });
        },
        [deleteApplicationDraft, dislikeJob, indeedBaseUrl],
    );
};
